import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";

/**
 * Phase 6 (Feb 2026) — Registration now lands in pending_approval.
 * The endpoint returns 202 + a payload describing the request; we
 * render a confirmation screen instead of routing straight to
 * /app/dashboard. The user is told they'll receive access once a
 * platform operator approves.
 */
export default function Register() {
  const t = useT();
  const [form, setForm] = useState({
    tenant_slug: "", tenant_name: "", email: "", password: "",
    full_name: "", context: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_BASE}/auth/register`, { ...form, locale: "de" });
      setSubmitted(res.data);
      toast.success("Antrag eingereicht");
    } catch (err) {
      const d = err?.response?.data;
      let msg = "Registration failed";
      if (d?.detail && typeof d.detail === "string") msg = d.detail;
      else if (d?.detail?.error === "rate_limited")
        msg = "Zu viele Registrierungen — bitte später erneut versuchen.";
      else if (d?.message) msg = d.message;
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-4">
        <Card className="w-full max-w-md" data-testid="register-submitted-card">
          <CardHeader>
            <CardTitle data-testid="register-submitted-title">
              Antrag eingereicht
            </CardTitle>
            <CardDescription>
              Wir prüfen deinen Zugang.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p
              className="text-sm"
              data-testid="register-submitted-message"
            >
              {submitted.message || "Antrag eingereicht — wird durch Plattform-Betreiber geprüft."}
            </p>
            <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1"
                  data-testid="register-submitted-details">
              <div><b>Firma:</b> {form.tenant_name}</div>
              <div><b>Kürzel:</b> <code className="font-mono">{submitted.tenant_slug}</code></div>
              <div><b>Kontakt:</b> {form.email}</div>
            </div>
            <p className="text-xs text-muted-foreground">
              Sobald der Plattform-Betreiber freigegeben hat, kannst du
              dich mit deiner E-Mail-Adresse und dem gewählten Passwort
              einloggen. Bis dahin wird der Login mit einem klaren
              Hinweis abgelehnt.
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login" data-testid="register-submitted-back-to-login">
                Zurück zum Login
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4">
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader>
          <CardTitle>{t("auth.sign_up")}</CardTitle>
          <CardDescription>
            {t("auth.subtitle")} · Freigabe durch Plattform-Betreiber erforderlich.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label>{t("auth.workspace_name")}</Label>
              <Input data-testid="register-tenant-name" value={form.tenant_name} onChange={set("tenant_name")} required />
            </div>
            <div className="space-y-2">
              <Label>{t("auth.tenant")}</Label>
              <Input data-testid="register-tenant-slug" value={form.tenant_slug} onChange={set("tenant_slug")} required />
            </div>
            <div className="space-y-2">
              <Label>{t("auth.full_name")}</Label>
              <Input data-testid="register-full-name" value={form.full_name} onChange={set("full_name")} />
            </div>
            <div className="space-y-2">
              <Label>{t("auth.email")}</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={set("email")} required />
            </div>
            <div className="space-y-2">
              <Label>{t("auth.password")}</Label>
              <Input data-testid="register-password" type="password" minLength={8} value={form.password} onChange={set("password")} required />
            </div>
            <div className="space-y-2">
              <Label>Kontext für Freigabe (optional)</Label>
              <Textarea
                data-testid="register-context"
                value={form.context}
                onChange={set("context")}
                rows={3}
                maxLength={1000}
                placeholder="Kurz: Was möchtest du mit VIONA erreichen? Für welche Firma?"
              />
              <p className="text-xs text-muted-foreground">
                Hilft dem Plattform-Betreiber, deinen Antrag schneller zu bewerten.
              </p>
            </div>
            <Button type="submit" disabled={busy} className="w-full" data-testid="register-submit">
              {busy ? t("common.loading") : "Antrag einreichen"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {t("auth.has_account")} <Link to="/login" className="underline" data-testid="link-login">{t("auth.sign_in")}</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
